import { ErrorHandler, Injectable, inject, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '../../shared/services/notification.service';
import { LoggerService } from '../../shared/services/logger.service';

/**
 * ErrorHandler global que captura exceções não tratadas.
 *
 * Comportamento:
 * - Erros de rede (status 0 ou offline): silencioso
 * - 5xx do servidor: notificação de erro + log estruturado
 * - 4xx: log de aviso (auth já tratado nos guards/interceptors)
 * - Erros de chunk lazy: notificação para recarregar página
 * - Outros erros JS: log apenas (snackbar genérico temporariamente desabilitado)
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly notifications = inject(NotificationService);
  private readonly logger = inject(LoggerService);
  private readonly zone = inject(NgZone);

  /** Evita empilhar o snackbar de chunk error quando o import falha repetidamente. */
  private chunkErrorNotificado = false;

  handleError(error: unknown): void {
    this.zone.run(() => {
      if (error instanceof HttpErrorResponse) {
        this.handleHttpError(error);
      } else {
        this.handleJsError(error);
      }
    });
  }

  private handleHttpError(err: HttpErrorResponse): void {
    if (err.status === 0 || !navigator.onLine) {
      return;
    }

    if (err.status >= 500) {
      this.logger.error('HTTP 5xx não tratado', { status: err.status, url: err.url });
      this.notifications.error('Erro no servidor. Tente novamente em instantes.');
    } else if (err.status === 401 || err.status === 403) {
      // Não notificar — auth guard/interceptor já lida com 401/403
    } else if (err.status >= 400) {
      this.logger.warn('HTTP 4xx não tratado', { status: err.status, url: err.url });
    }
  }

  private handleJsError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    const isChunkError =
      /Loading chunk/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message);

    if (isChunkError) {
      this.logger.warn('Erro de carregamento de módulo (possível deploy)', { message });
      if (!this.chunkErrorNotificado) {
        this.chunkErrorNotificado = true;
        this.notifications.warnComAcao(
          'Atualização disponível. Recarregue a página para continuar.',
          'Recarregar',
          () => document.location.reload(),
        );
      }
      return;
    }

    console.error('!!! FULL ERROR !!!', error);
    this.logger.error('Erro JavaScript não tratado', { message });
    // TODO: reabilitar snackbar genérico após identificar a causa raiz do erro
    // no step "Local da Solicitação" (wizard RAP). Mantido desligado em todos
    // os ambientes para não expor falha técnica ao policial em campo.
    // this.notifications.error('Ocorreu um erro inesperado. Se persistir, contate o suporte.');
  }
}
